import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '../components/Layout.tsx';
import { apiClient } from '../api/client.ts';
import {
  Loader2,
  Users,
  Lightbulb,
  Mail,
  Bell,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface EmailLogItem {
  id: string;
  type: string;
  recipient: string;
  subject: string;
  status: string;
  attempts: number;
  error: string | null;
  queuedAt: string;
  sentAt: string | null;
  deliveredAt: string | null;
  failedAt: string | null;
}

const STATUS_STYLES: Record<string, string> = {
  QUEUED: 'bg-gray-100 text-gray-700',
  SENT: 'bg-blue-100 text-blue-700',
  DELIVERED: 'bg-green-100 text-green-700',
  BOUNCED: 'bg-orange-100 text-orange-700',
  COMPLAINED: 'bg-purple-100 text-purple-700',
  FAILED: 'bg-red-100 text-red-700',
};

function KpiCard({ label, value, accent }: { label: string; value: string | number | null; accent?: string }) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
      <p className="text-xs text-gray-500 uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-bold mt-1 ${accent ?? 'text-gray-900'}`}>
        {value === null || value === undefined ? '—' : value}
      </p>
    </div>
  );
}

function SectionTitle({ icon: Icon, title }: { icon: any; title: string }) {
  return (
    <div className="flex items-center gap-2 mt-8 mb-3">
      <Icon className="w-5 h-5 text-[#c29547]" />
      <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
    </div>
  );
}

export function Reports() {
  const [logType, setLogType] = useState('');
  const [logStatus, setLogStatus] = useState('');
  const [page, setPage] = useState(1);

  const { data: kpis, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['reportKpis'],
    queryFn: async () => {
      const res = await apiClient.get('/admin/reports/kpis');
      return res.data.data;
    },
    refetchInterval: 30000,
  });

  const { data: logs, isLoading: logsLoading } = useQuery({
    queryKey: ['emailLogs', logType, logStatus, page],
    queryFn: async () => {
      const res = await apiClient.get('/admin/reports/email-logs', {
        params: { type: logType || undefined, status: logStatus || undefined, page, limit: 25 },
      });
      return res.data.data;
    },
    refetchInterval: 30000,
  });

  if (isLoading) {
    return (
      <Layout title="Reports & KPIs">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#c29547]" />
        </div>
      </Layout>
    );
  }

  const a = kpis?.auth ?? {};
  const i = kpis?.ideas ?? {};
  const e = kpis?.email ?? {};
  const n = kpis?.notifications ?? {};

  return (
    <Layout title="Reports & KPIs">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Reports & KPIs</h1>
            <p className="text-sm text-gray-500">
              Live metrics across auth, ideas, email delivery and notifications
              {kpis?.generatedAt ? ` · updated ${new Date(kpis.generatedAt).toLocaleTimeString()}` : ''}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* ---- Auth & Users ---- */}
        <SectionTitle icon={Users} title="Users & Authentication" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Users" value={a.totalUsers} />
          <KpiCard label="Employees" value={a.totalEmployees} />
          <KpiCard label="Admins" value={a.totalAdmins} />
          <KpiCard label="Approval Team" value={a.approvalTeamMembers} />
          <KpiCard label="Authenticated" value={a.authenticatedUsers} accent="text-green-600" />
          <KpiCard label="Pending Auth" value={a.pendingAuthUsers} accent="text-orange-600" />
          <KpiCard label="Auth Completion" value={a.authCompletionRatePct != null ? `${a.authCompletionRatePct}%` : null} />
          <KpiCard label="Active 24h" value={a.activeUsers24h} />
          <KpiCard label="Active 7d" value={a.activeUsers7d} />
          <KpiCard label="Active 30d" value={a.activeUsers30d} />
          <KpiCard label="New Users 7d" value={a.newUsers7d} />
          <KpiCard label="New Users 30d" value={a.newUsers30d} />
        </div>

        {/* ---- Ideas ---- */}
        <SectionTitle icon={Lightbulb} title="Ideas Pipeline" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Ideas" value={i.totalIdeas} />
          <KpiCard label="Draft" value={i.draft} />
          <KpiCard label="Submitted" value={i.submitted} accent="text-blue-600" />
          <KpiCard label="Under Review" value={i.underReview} accent="text-yellow-600" />
          <KpiCard label="Approved" value={i.approved} accent="text-green-600" />
          <KpiCard label="Rejected" value={i.rejected} accent="text-red-600" />
          <KpiCard label="More Info Requested" value={i.moreInfoRequested} />
          <KpiCard label="New 24h" value={i.ideas24h} />
          <KpiCard label="New 7d" value={i.ideas7d} />
          <KpiCard label="New 30d" value={i.ideas30d} />
          <KpiCard label="Approval Rate" value={i.approvalRatePct != null ? `${i.approvalRatePct}%` : null} />
          <KpiCard label="Avg Decision (days)" value={i.avgDecisionDays} />
          <KpiCard label="With Attachments" value={i.ideasWithAttachments} />
          <KpiCard label="Attachments" value={i.totalAttachments} />
          <KpiCard label="Comments" value={i.totalComments} />
        </div>

        {(i.topDepartments?.length || i.topCategories?.length) ? (
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Top Departments</p>
              {(i.topDepartments ?? []).map((d: any) => (
                <div key={d.name} className="flex justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                  <span className="text-gray-600">{d.name}</span>
                  <span className="font-medium">{d.count}</span>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <p className="text-sm font-semibold text-gray-700 mb-2">Top Categories</p>
              {(i.topCategories ?? []).map((c: any) => (
                <div key={c.name} className="flex justify-between text-sm py-1 border-b border-gray-50 last:border-0">
                  <span className="text-gray-600">{c.name}</span>
                  <span className="font-medium">{c.count}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {/* ---- Email & OTP ---- */}
        <SectionTitle icon={Mail} title="Email & OTP Delivery (last 24h)" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard label="Emails Queued" value={e.emailsQueued24h} />
          <KpiCard label="Emails Sent" value={e.emailsSent24h} />
          <KpiCard label="Delivered" value={e.emailsDelivered24h} accent="text-green-600" />
          <KpiCard label="Failed" value={e.emailsFailed24h} accent="text-red-600" />
          <KpiCard label="Bounced" value={e.emailsBounced24h} accent="text-orange-600" />
          <KpiCard label="Delivery Rate" value={e.deliveryRate24hPct != null ? `${e.deliveryRate24hPct}%` : null} />
          <KpiCard label="OTP Requested" value={e.otpRequested24h} />
          <KpiCard label="OTP Sent" value={e.otpSent24h} />
          <KpiCard label="OTP Delivered" value={e.otpDelivered24h} accent="text-green-600" />
          <KpiCard label="OTP Failed" value={e.otpFailed24h} accent="text-red-600" />
          <KpiCard label="OTP Delivery Rate" value={e.otpDeliveryRate24hPct != null ? `${e.otpDeliveryRate24hPct}%` : null} />
          <KpiCard label="Queue Backlog" value={e.pendingEmailBacklog} accent={e.pendingEmailBacklog > 0 ? 'text-orange-600' : 'text-gray-900'} />
          <KpiCard label="SES Quota Used" value={e.sesQuotaUsedPct != null ? `${e.sesQuotaUsedPct}%` : null} />
          <KpiCard label="SES Daily Quota" value={e.sesDailyQuota} />
        </div>

        {/* ---- Notifications ---- */}
        <SectionTitle icon={Bell} title="Notifications" />
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Notifications" value={n.totalNotifications} />
          <KpiCard label="Unread" value={n.unreadNotifications} />
          <KpiCard label="Read Rate" value={n.readRatePct != null ? `${n.readRatePct}%` : null} />
        </div>

        {/* ---- Email delivery log ---- */}
        <SectionTitle icon={Mail} title="Email Delivery Log" />
        <div className="flex gap-3 mb-3">
          <select
            value={logType}
            onChange={(ev) => { setLogType(ev.target.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">All types</option>
            <option value="OTP">OTP</option>
            <option value="NOTIFICATION">Notification</option>
            <option value="DIGEST">Digest</option>
            <option value="OTHER">Other</option>
          </select>
          <select
            value={logStatus}
            onChange={(ev) => { setLogStatus(ev.target.value); setPage(1); }}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white"
          >
            <option value="">All statuses</option>
            {Object.keys(STATUS_STYLES).map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          {logsLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="w-6 h-6 animate-spin text-[#c29547]" />
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-3">Recipient</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Attempts</th>
                  <th className="px-4 py-3">Queued</th>
                  <th className="px-4 py-3">Sent</th>
                  <th className="px-4 py-3">Delivered</th>
                  <th className="px-4 py-3">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(logs?.items ?? []).map((row: EmailLogItem) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium text-gray-800">{row.recipient}</td>
                    <td className="px-4 py-2 text-gray-600">{row.type}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[row.status] ?? 'bg-gray-100 text-gray-700'}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{row.attempts}</td>
                    <td className="px-4 py-2 text-gray-500">{new Date(row.queuedAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-gray-500">{row.sentAt ? new Date(row.sentAt).toLocaleTimeString() : '—'}</td>
                    <td className="px-4 py-2 text-gray-500">{row.deliveredAt ? new Date(row.deliveredAt).toLocaleTimeString() : '—'}</td>
                    <td className="px-4 py-2 text-red-500 max-w-xs truncate" title={row.error ?? ''}>{row.error ?? '—'}</td>
                  </tr>
                ))}
                {(logs?.items ?? []).length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400">
                      No email logs match the selected filters yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {logs?.meta && logs.meta.totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 text-sm text-gray-600">
            <span>
              Page {logs.meta.page} of {logs.meta.totalPages} · {logs.meta.total} records
            </span>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                className="p-2 border border-gray-300 rounded-lg bg-white disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                disabled={page >= logs.meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="p-2 border border-gray-300 rounded-lg bg-white disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
